import { useMemo, useState } from "react";
import { selectableUsers } from "@/lib/userDirectory";
import { Check, ChevronDown, X, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchClearButton from "@/components/ui/SearchClearButton";

const ROLES = [
  { v: "executor", l: "Exécuteur" },
  { v: "manager", l: "Manager" },
  { v: "helper", l: "Aide exécuteur" },
];

/**
 * Multi-select assignee picker with a role per member.
 * `value` = [{user_id, role}], `users` = full user list.
 */
export default function AssigneeSelector({ users, value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return selectableUsers(users).filter((u) => {
      const label = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
      return !s || label.includes(s);
    });
  }, [users, q]);

  const isPicked = (uid) => value.some((v) => v.user_id === uid);
  const toggle = (uid) => {
    if (isPicked(uid)) onChange(value.filter((v) => v.user_id !== uid));
    else onChange([...value, { user_id: uid, role: "executor" }]);
  };
  const setRole = (uid, role) => onChange(value.map((v) => (v.user_id === uid ? { ...v, role } : v)));
  const remove = (uid) => onChange(value.filter((v) => v.user_id !== uid));

  const findUser = (uid) => users.find((u) => u.id === uid);

  return (
    <div className="space-y-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between" data-testid="assignee-picker">
            <span className="text-muted-foreground">Sélectionner des personnes…</span>
            <ChevronDown size={14} />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[420px] p-0">
          <div className="p-2 border-b border-vertec-borderDark">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-8 pr-8 h-9" data-no-capitalize />
              <SearchClearButton value={q} onClear={() => setQ("")} testid="assignee-search-clear" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-4">Aucun utilisateur.</div>
            )}
            {filtered.map((u) => {
              const picked = isPicked(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-left"
                  data-testid={`assignee-option-${u.id}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${picked ? "bg-vertec-blue border-vertec-blue" : "border-vertec-borderDark"}`}>
                    {picked && <Check size={12} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-col gap-2">
          {value.map((v) => {
            const u = findUser(v.user_id);
            if (!u) return null;
            return (
              <div key={v.user_id} className="flex items-center gap-2 border border-vertec-borderDark rounded-md px-2 py-1.5">
                <Badge variant="secondary" className="truncate max-w-[180px]" title={`${u.first_name} ${u.last_name}`}>
                  {u.first_name} {u.last_name}
                </Badge>
                <Select value={v.role} onValueChange={(r) => setRole(v.user_id, r)}>
                  <SelectTrigger className="h-8 w-[160px] text-xs" data-testid={`assignee-role-${u.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button className="ml-auto text-muted-foreground hover:text-status-danger" onClick={() => remove(v.user_id)} aria-label="Retirer">
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
